import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { createNewShiftProgram } from './operationsSlice'
import {
  OpsPageWrapper,
  OpsCard,
  OpsCardHeader,
  StepperWrapper,
  StepItem,
  StepCircle,
  StepLabel,
  StepConnector,
  FormSection,
  FormLabel,
  FormInput,
  FormSelect,
  ReviewSection,
  ReviewLabel,
  ReviewValue,
  InfoBox,
  SuccessScreen,
  OpsButton,
  Badge,
  opsColors,
} from './styled/Operations.styled'

const STEPS = ['Program Scope', 'Task Assignments', 'Review']

interface TaskAssignment {
  id: string
  templateName: string
  operationName: string
  locations: string[]
  workersPerLocation: number
  notes: string
}

export const CreateShiftProgram: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const [step, setStep] = useState(0)
  const [created, setCreated] = useState(false)

  // Step 1
  const [programName, setProgramName] = useState('')
  const [siteId, setSiteId] = useState('')
  const [departmentId, setDepartmentId] = useState('')

  // Step 2
  const [assignments, setAssignments] = useState<TaskAssignment[]>([])

  const addMockAssignment = () => {
    setAssignments([...assignments, {
      id: `ta-${Date.now()}`,
      templateName: `Inspection Template ${assignments.length + 1}`,
      operationName: 'Routine Inspection',
      locations: ['Block A', 'Block B'],
      workersPerLocation: 1,
      notes: '',
    }])
  }

  const totalTasks = assignments.reduce((sum, a) => sum + a.locations.length * a.workersPerLocation, 0)
  const totalWorkers = assignments.reduce((sum, a) => sum + a.locations.length * a.workersPerLocation, 0)

  const handleCreate = async () => {
    await dispatch(createNewShiftProgram({
      name: programName,
      siteId: Number(siteId),
      departmentId: Number(departmentId),
      taskBindings: assignments,
    }))
    setCreated(true)
  }

  if (created) {
    return (
      <OpsPageWrapper>
        <SuccessScreen>
          <div style={{ fontSize: '48px', color: opsColors.successBase }}>&#10003;</div>
          <h2>{programName}</h2>
          <Badge bg={opsColors.successBase}>ACTIVE</Badge>
          <p>Shift program created and activated. Tasks will be generated at the next shift start.</p>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <OpsButton variant="secondary" onClick={() => navigate('/ops-control/shift-programs')}>Back to List</OpsButton>
          </div>
        </SuccessScreen>
      </OpsPageWrapper>
    )
  }

  return (
    <OpsPageWrapper>
      <h1 style={{ fontSize: '30px', fontWeight: 900, color: opsColors.textPrimary, margin: 0, fontFamily: 'DM Sans, sans-serif' }}>
        Create Shift Program
      </h1>

      <StepperWrapper>
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <StepItem>
              <StepCircle active={i === step} completed={i < step} onClick={() => i < step && setStep(i)} style={{ cursor: i < step ? 'pointer' : 'default' }}>
                {i < step ? '✓' : i + 1}
              </StepCircle>
              <StepLabel active={i === step}>{s}</StepLabel>
            </StepItem>
            {i < STEPS.length - 1 && <StepConnector />}
          </React.Fragment>
        ))}
      </StepperWrapper>

      <OpsCard>
        {step === 0 && (
          <FormSection>
            <OpsCardHeader><h3>Program Scope</h3></OpsCardHeader>
            <div>
              <FormLabel>Program Name</FormLabel>
              <FormInput value={programName} onChange={(e) => setProgramName(e.target.value)} placeholder="e.g. Daily Site Cleaning" />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <FormLabel>Site</FormLabel>
                <FormSelect value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                  <option value="">Select site...</option>
                  <option value="1">Site Alpha</option>
                  <option value="2">Site Beta</option>
                </FormSelect>
              </div>
              <div>
                <FormLabel>Department *</FormLabel>
                <FormSelect value={departmentId} onChange={(e) => setDepartmentId(e.target.value)}>
                  <option value="">Select department...</option>
                  <option value="4">Maintenance</option>
                  <option value="5">Camp Services</option>
                  <option value="6">Security</option>
                  <option value="1">Operations</option>
                </FormSelect>
              </div>
            </div>
          </FormSection>
        )}

        {step === 1 && (
          <FormSection>
            <OpsCardHeader><h3>Task Assignments</h3></OpsCardHeader>
            <OpsButton variant="secondary" onClick={addMockAssignment}>+ Add Task Template</OpsButton>

            {assignments.map((a, i) => (
              <OpsCard key={a.id} style={{ background: opsColors.bgPrimary }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                  <div>
                    <span style={{ fontWeight: 700, color: opsColors.textPrimary }}>{a.templateName}</span>
                    <span style={{ fontSize: '12px', color: opsColors.textFaded, marginLeft: '8px' }}>{a.operationName}</span>
                  </div>
                  <OpsButton variant="danger" size="sm" onClick={() => setAssignments(assignments.filter((_, j) => j !== i))}>Remove</OpsButton>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <FormLabel>Locations</FormLabel>
                    <div style={{ fontSize: '13px', color: opsColors.textSecondary }}>
                      {a.locations.join(', ')}
                    </div>
                  </div>
                  <div>
                    <FormLabel>Workers per Location</FormLabel>
                    <FormInput type="number" value={a.workersPerLocation} onChange={(e) => {
                      const updated = [...assignments]
                      updated[i] = { ...updated[i], workersPerLocation: Number(e.target.value) }
                      setAssignments(updated)
                    }} />
                  </div>
                </div>
                <div style={{ marginTop: '0.5rem', fontSize: '12px', color: opsColors.brand500 }}>
                  {a.locations.length} locations x {a.workersPerLocation} worker{a.workersPerLocation > 1 ? 's' : ''} = {a.locations.length * a.workersPerLocation} tasks
                </div>
              </OpsCard>
            ))}

            {assignments.length > 0 && (
              <InfoBox variant="info">
                Total per shift: {totalTasks} tasks, {totalWorkers} workers needed
              </InfoBox>
            )}
          </FormSection>
        )}

        {step === 2 && (
          <FormSection>
            <OpsCardHeader><h3>Review & Activate</h3></OpsCardHeader>
            <ReviewSection>
              <ReviewLabel>Program</ReviewLabel>
              <ReviewValue>{programName || 'Not set'}</ReviewValue>
            </ReviewSection>
            <ReviewSection>
              <ReviewLabel>Tasks</ReviewLabel>
              <ReviewValue>{assignments.length} template{assignments.length !== 1 ? 's' : ''}, {totalTasks} tasks per shift</ReviewValue>
            </ReviewSection>
            <InfoBox variant="info">
              Shift Programs activate immediately. Tasks will be generated at the next shift start.
            </InfoBox>
            <div style={{ fontSize: '12px', color: opsColors.textFaded }}>
              Provenance: source: SHIFT_PROGRAM
            </div>
          </FormSection>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1.5rem' }}>
          <OpsButton variant="secondary" onClick={() => step > 0 ? setStep(step - 1) : navigate('/ops-control/shift-programs')}>
            {step > 0 ? 'Previous' : 'Cancel'}
          </OpsButton>
          {step < STEPS.length - 1 ? (
            <OpsButton onClick={() => setStep(step + 1)}>Next</OpsButton>
          ) : (
            <OpsButton onClick={handleCreate}>Create & Activate</OpsButton>
          )}
        </div>
      </OpsCard>
    </OpsPageWrapper>
  )
}
